import { Dispatch, SetStateAction, useEffect, useRef } from "react";
import { Popover, Whisper } from "rsuite";
import { Button } from "reactstrap";

import DropdownOptionWithIcon from "../../Menus/DropdownOptionWithIcon";
import PopoverDropdownMenu from "../../Menus/PopoverDropdownMenu";
import DropdownMenuDivider from "../../Menus/DropdownMenuDivider";
import { WidgetType } from "../../../types/DashboardInterfaces";

interface DashboardToolbarPrpops {
  /** Callback for when a widget is added. */
  addWidget: (type: WidgetType, label: string) => void;
  /** Whether or not toolbar is visible */
  editMode: boolean;
  /** External state value for opening add widget */
  openAddWidget: boolean;
  /** External state setter for openAddWidget */
  setOpenAddWidget: Dispatch<SetStateAction<boolean>>;
  insightsEnabled: boolean;
}

const DashboardToolbar = ({
  addWidget,
  editMode,
  openAddWidget,
  setOpenAddWidget,
  insightsEnabled,
}: DashboardToolbarPrpops) => {
  const addWidgetWhisper = useRef<any>();

  // For externally opening Add Widget whisper.
  useEffect(() => {
    if (openAddWidget) {
      addWidgetWhisper?.current?.open();

      setOpenAddWidget(false);
    }
  }, [openAddWidget, setOpenAddWidget]);

  if (!editMode) return null;

  return (
    <div className="d-flex mb-1">
      <Whisper
        placement="autoVerticalStart"
        trigger="click"
        ref={addWidgetWhisper}
        preventOverflow
        speaker={
          <Popover
            className="sidebar-popover"
            full
            onClick={() => addWidgetWhisper.current.close()}
            style={{ width: 200 }}
          >
            <PopoverDropdownMenu>
              <h5 className="px-2 pt-2 text-dark">
                <i className="mdi mdi-widgets pr-2" />
                Widgets
              </h5>
              <DropdownMenuDivider />
              <DropdownOptionWithIcon
                name="Pie Chart"
                onClick={() => addWidget("PieChart", "Pie Chart")}
                icon="mdi mdi-chart-pie"
              />
              <DropdownOptionWithIcon
                name="Donut Chart"
                onClick={() => addWidget("DonutChart", "Donut Chart")}
                icon="mdi mdi-chart-donut"
              />
              <DropdownOptionWithIcon
                name="Bar Chart"
                onClick={() => addWidget("BarChart", "Bar Chart")}
                icon="mdi mdi-chart-bar"
              />
              <DropdownOptionWithIcon
                name="Grouped Bar Chart"
                onClick={() =>
                  addWidget("GroupedBarChart", "Grouped Bar Chart")
                }
                icon="mdi mdi-chart-bar-stacked"
              />
              <DropdownOptionWithIcon
                name="Progress Bar Chart"
                onClick={() =>
                  addWidget("ProgressBarChart", "Progress Bar Chart")
                }
                icon="mdi mdi-glass-stange"
              />
              <DropdownOptionWithIcon
                name="Line Chart"
                onClick={() => addWidget("LineChart", "Line Chart")}
                icon="mdi mdi-chart-line"
              />
              <DropdownOptionWithIcon
                name="Grouped Line Chart"
                onClick={() =>
                  addWidget("GroupedLineChart", "Grouped Line Chart")
                }
                icon="mdi mdi-chart-line-stacked"
              />
              <DropdownOptionWithIcon
                name="Stage Funnel Chart"
                onClick={() => addWidget("StageFunnel", "Stage Funnel Chart")}
                icon="mdi mdi-stairs"
              />
              <DropdownOptionWithIcon
                name="Filled Line Chart"
                onClick={() =>
                  addWidget("FilledLineChart", "Filled Line Chart")
                }
                icon="mdi mdi-chart-areaspline-variant"
              />
              <DropdownOptionWithIcon
                name="Single Stat"
                onClick={() => addWidget("SingleStat", "Single Stat")}
                icon="mdi mdi-numeric"
              />
              <DropdownOptionWithIcon
                name="Document"
                onClick={() => addWidget("Document", "Document")}
                icon="mdi mdi-file-document-outline"
              />
              {!insightsEnabled && (
                <DropdownOptionWithIcon
                  name="Table"
                  onClick={() => addWidget("Table", "Table")}
                  icon="mdi mdi-table-large"
                />
              )}
              <DropdownOptionWithIcon
                name="Header"
                onClick={() => addWidget("Header", "Header")}
                icon="mdi mdi-format-header-1"
              />
              <DropdownOptionWithIcon
                name="Map"
                onClick={() => addWidget("Map", "Map")}
                icon="mdi mdi-map"
              />
              {insightsEnabled && (
                <DropdownOptionWithIcon
                  name="Flow Builder"
                  onClick={() => addWidget("FlowChart", "Flow Chart")}
                  icon="mdi mdi-sitemap"
                />
              )}
            </PopoverDropdownMenu>
          </Popover>
        }
      >
        <Button size="sm" color="primary">
          <span style={{ position: "relative", top: -4 }}>Add Widget</span>
          <i
            className="mdi mdi-chevron-down ml-2 mr--1"
            style={{ fontSize: 22, lineHeight: 1 }}
          />
        </Button>
      </Whisper>
    </div>
  );
};

export default DashboardToolbar;
